import "@/modules/support/chat.css";

import { chatConfigAtom, configAtom } from "@/config.ts";
import { showWarn } from "@/modules/error/errorStream.ts";
import { getThemeVar_ONCE } from "@/modules/theme/useTheme.ts";
import { isImpersonatingAtom } from "@/modules/user/impersonation.store.ts";
import { userDataPromiseAtom } from "@/modules/user/userApi.ts";
import { cleanTimeout } from "@/utils/cleanTimeout.ts";
import { loadScript } from "@/utils/loadScript.ts";
import { makePromise } from "@/utils/promise/makePromise.ts";
import * as intercom from "@intercom/messenger-js-sdk";
import type { IntercomSettings } from "@intercom/messenger-js-sdk/dist/types";
import { t } from "@lingui/core/macro";
import type {
    BotikaConfig,
    IntercomConfig,
    JivochatConfig,
    LeadBoosterConfig,
    OncehubConfig,
    QiscusConfig,
    TawkConfig,
    User,
} from "@warrenio/api-spec/spec.oats.gen";
import { makeOnce } from "@warrenio/utils/makeOnce";
import { notNull } from "@warrenio/utils/notNull";
import { discardPromise } from "@warrenio/utils/promise/discardPromise";
import { useAtomValue } from "jotai/react";
import { atom } from "jotai/vanilla";
import { useEffect } from "react";

export interface ChatProvider {
    load(): Promise<void>;
    /** Open the messenger interface */
    open(): void;
    /** Show/hide the messenger completely (in eg. admin)*/
    setVisible?(visible: boolean): void;
}

/* eslint-disable @typescript-eslint/no-namespace */

//
// CONVENTIONS:
//
// - All the factory functions are wrapped in `makeOnce` since configuration of the chat providers should only happen once. (even if eg. the config changes due to hot reload)
// - The `load` method is also wrapped so that the chat provider is only loaded once (and the promise can be awaited without checking)
//

//#region Qiscus

declare global {
    class Qismo {
        constructor(key: string, options: object);
    }
}

const qiscusFactory = makeOnce(
    ({ qiscus_channel_id, qiscus_client_key }: QiscusConfig): ChatProvider => ({
        load: makeOnce(async () => {
            await loadScript("https://s3-ap-southeast-1.amazonaws.com/qiscus-sdk/public/qismo/qismo-v4.js");

            const _qismo = new Qismo(qiscus_client_key, {
                options: {
                    channel_id: qiscus_channel_id,
                    extra_fields: [],
                },
            });
        }),
        open() {
            const btn = document.querySelector<HTMLElement>(".qcw-trigger-btn");
            btn?.click();
        },
    }),
);

//#endregion

//#region Botika
// Documentation (no JavaScript API!): https://omni.botika.online/help-center/integrations/webchat.html
declare global {
    namespace BotikaChat {
        function init(options: object): unknown;
    }
}

const botikaFactory = makeOnce(
    ({ botika_client_key }: BotikaConfig, primaryColor: string, logoImage: string, siteName: string): ChatProvider => ({
        load: makeOnce(async () => {
            await loadScript("//chat.botika.online/client/assets/js/botika.widget.js");

            BotikaChat.init({
                client: botika_client_key,
                widget: {
                    theme: "custom",
                    "theme-color": primaryColor,
                    "bubble-color": primaryColor,
                    "description-color": "#ffffff",
                    title: siteName,
                    description: t`Customer support`,
                    caption: t`Need help?`,
                    greeting: true,
                    logo: logoImage,
                    history: true,
                    "greeting-message": t({ comment: "Chat widget greeting", message: "Hello" }),
                    "greeting-button": t`Start`,
                    autocomplete: "",
                    registerUser: { name: true, email: true, phone: false },
                    startMessage: "",
                    clientCustom: "",
                    buttonPayload: true,
                    voice: { active: false, disabled: true },
                },
            });
        }),
        open() {
            warnNotImplemented();
        },
    }),
);
//#endregion

//#region Oncehub
// Documentation (no JavaScript API!): https://help.oncehub.com/help/chatbots#adding-chatbots-to-your-website

const oncehubFactory = makeOnce(
    ({ oncehub_website_id, oncehub_bot_id }: OncehubConfig): ChatProvider => ({
        load: makeOnce(async () => {
            await loadScript("https://cdn.oncehub.com/co/widget.js", (script) => {
                script.id = "co-index";
                script.dataset.coParams = `website_id=${oncehub_website_id}&bot_type=2`;
            });

            const link = document.createElement("a");
            link.id = "oncechat-link";
            link.href = "#";
            link.dataset.coBot = oncehub_bot_id;
            link.setAttribute("onclick", "document.getElementById('oncechat-link').style.display='none'");
            document.body.appendChild(link);
        }),
        open() {
            const btn = document.querySelector<HTMLElement>("#oncechat-link");
            btn?.click();
        },
    }),
);

//#endregion

//#region LeadBooster
// Documentation: https://pipedrive.readme.io/docs/leadbooster-browser-api

declare global {
    interface Window {
        pipedriveLeadboosterConfig?: {
            base: string;
            companyId: string;
            playbookUuid: string;
            version: number;
        };
        LeadBooster: {
            q: unknown[];
            on(n: unknown, h: unknown): void;
            trigger(n: "open" | "close"): void;
        };
    }
}

const leadBoosterFactory = makeOnce(
    ({ company_id, playbook_uuid }: LeadBoosterConfig): ChatProvider => ({
        load: makeOnce(async () => {
            if (window.LeadBooster) {
                console.warn("LeadBooster already exists");
            } else {
                // Dummy implementation while loading
                window.LeadBooster = {
                    q: [],
                    on(n, h) {
                        this.q.push({ t: "o", n, h });
                    },
                    trigger(n) {
                        this.q.push({ t: "t", n });
                    },
                };
            }

            window.pipedriveLeadboosterConfig = {
                base: "leadbooster-chat.pipedrive.com",
                companyId: company_id,
                playbookUuid: playbook_uuid,
                version: 2,
            };

            await loadScript("https://leadbooster-chat.pipedrive.com/assets/loader.js");
        }),
        open() {
            window.LeadBooster.trigger("open");
        },
    }),
);

//#endregion

//#region Intercom
// Documentation: https://developers.intercom.com/installing-intercom/web/methods

// XXX: We should use `user_id` instead of `email` for identifying the user, but that is not backward compatible.
// It also requires changing the server-side HMAC.
type IntercomUser = Pick<IntercomSettings, "email" | "user_hash" | "name">;

const intercomFactory = makeOnce(
    (
        { intercom_app_id }: IntercomConfig,
        primaryColor: string,
        userPromise: Promise<IntercomUser | undefined>,
    ): ChatProvider => ({
        load: makeOnce(async () => {
            // NB: Initialized asynchronously while waiting for the user data to load
            const user = await userPromise;
            intercom.Intercom({
                app_id: intercom_app_id,
                alignment: "left",
                action_color: primaryColor,
                background_color: primaryColor,
                // TODO: Should we always send the email to Intercom (or should we first inform the user?)
                ...user,
            });
        }),
        open() {
            intercom.show();
        },
    }),
);

const intercomUserAtom = atom(async (get): Promise<IntercomUser | undefined> => {
    const chatUser = await get(chatUserAtom);
    if (!chatUser) return undefined;

    const { intercom_user_hash, name, email } = chatUser;
    if (!intercom_user_hash) {
        console.debug("Intercom secret not configured on the backend, not sending email to Intercom");
        return { name };
    } else {
        // NB: `email` can only be sent together with `user_hash`
        return { email, user_hash: intercom_user_hash, name };
    }
});

//#endregion

//#region Tawk
// Documentation: https://developer.tawk.to/jsapi/

declare global {
    interface Window {
        Tawk_API?: {
            maximize(): void;
            minimize(): void;
            toggle(): void;

            showWidget(): void;
            hideWidget(): void;

            visitor?: {
                name: string;
                email: string;
            };
            login(userData: { hash: string; userId: string; name?: string; email?: string; phone?: string }): void;
        };
    }
}

const tawkFactory = makeOnce(
    ({ widget_id, property_id }: TawkConfig): ChatProvider => ({
        load: makeOnce(async () => {
            await loadScript(`https://embed.tawk.to/${property_id}/${widget_id}`);
            // TODO: Pass user data to Tawk?
        }),
        open() {
            window.Tawk_API?.maximize();
        },
        setVisible(visible) {
            if (visible) window.Tawk_API?.showWidget();
            else window.Tawk_API?.hideWidget();
        },
    }),
);

//#endregion

//#region Jivochat
// Documentation: https://www.jivochat.com/docs/widget/

interface JivochatContactInfo {
    name?: string;
    email?: string;
    phone?: string;
    description?: string;
}

interface JivoCustomData {
    key?: string;
    content: string;
    title?: string;
    link?: string;
}

declare global {
    interface Window {
        jivo_api?: {
            open(params?: { start: "call" }): unknown;
            close(): { result?: "fail" };

            setContactInfo(info: JivochatContactInfo): unknown;
            setCustomData(data: JivoCustomData[]): unknown;

            sendPageTitle(title: string, fromApi?: boolean, url?: string): unknown;
            // TODO: Set an appropriate color
            setWidgetColor(color: string, color2?: string): unknown;
        };
        jivo_init: () => void;
        jivo_destroy: () => void;

        jivo_onLoadCallback?: () => void;
    }
}

const jivochatFactory = makeOnce(
    ({ widget_id }: JivochatConfig, userPromise: Promise<UserData | undefined>): ChatProvider => ({
        load: makeOnce(async () => {
            // NB: The API object is only available after the callback executes, NOT after the script loads
            const loadedPromise = makePromise<void>();
            window.jivo_onLoadCallback = loadedPromise.resolve;

            await loadScript(`//code.jivosite.com/widget/${widget_id}`);
            await loadedPromise;

            console.debug("JivoChat widget loaded");
            // Set contact info after the widget loads
            const user = await userPromise;
            if (user) {
                const jivo_api = notNull(window.jivo_api);
                jivo_api.setContactInfo(user);
                jivo_api.setCustomData([{ key: "User ID", content: user.id.toString() }]);
            }
        }),
        open() {
            window.jivo_api?.open();
        },
        setVisible(visible: boolean) {
            if (visible) window.jivo_init();
            else window.jivo_destroy();
        },
    }),
);

//#endregion

interface UserData extends User {
    name: string;
    email: string;
}

/** Shared atom that extracts common user data for chat providers */
const chatUserAtom = atom(async (get): Promise<UserData | undefined> => {
    const userData = await get(userDataPromiseAtom);
    if (!userData) {
        // Logged out user
        return undefined;
    }
    const {
        profile_data: { email, first_name, last_name },
    } = userData;
    const name = `${first_name ?? ""} ${last_name ?? ""}`.trim();
    return {
        ...userData,
        name,
        email: notNull(email),
    };
});

export const chatProviderAtom = atom((get): ChatProvider | undefined => {
    const chatConfig = get(chatConfigAtom);
    if (!chatConfig || !("type" in chatConfig)) {
        return undefined;
    }

    if (get(isImpersonatingAtom)) {
        // Prevent loading chat provider while impersonating (for less confusion and security, should also enforce this on the server side)
        return undefined;
    }

    const { siteName } = get(configAtom);
    const primaryColor = getThemeVar_ONCE("color-primary");
    const logoImage = getThemeVar_ONCE("logo-image");

    const { type } = chatConfig;
    switch (type) {
        case "intercom": {
            return intercomFactory(chatConfig, primaryColor, get(intercomUserAtom));
        }
        case "qiscus": {
            return qiscusFactory(chatConfig);
        }
        case "botika": {
            return botikaFactory(chatConfig, primaryColor, logoImage, siteName);
        }
        case "oncehub": {
            return oncehubFactory(chatConfig);
        }
        case "leadbooster": {
            return leadBoosterFactory(chatConfig);
        }
        case "tawk": {
            return tawkFactory(chatConfig);
        }
        case "jivochat": {
            return jivochatFactory(chatConfig, get(chatUserAtom));
        }
        default: {
            showWarn("Unsupported chat provider type", type);
            return undefined;
        }
    }
});

/** Delay loading the chat provider to improve initial page load performance */
const CHAT_INIT_DELAY_MS = 1500;

function useChatProvider(enabled = true) {
    const chatProvider = useAtomValue(chatProviderAtom);
    useEffect(
        () =>
            cleanTimeout(() => {
                if (enabled && chatProvider) {
                    console.debug("Loading chat provider");
                    discardPromise(chatProvider.load());
                }
            }, CHAT_INIT_DELAY_MS),
        [chatProvider, enabled],
    );

    // CSS hack to hide the chat widget in eg. admin mode
    useEffect(() => {
        document.body.classList.toggle("hideChat", !enabled);
    }, [enabled]);
}

function WChatLoader({ enabled }: { enabled?: boolean }) {
    useChatProvider(enabled);
    return null;
}

export default WChatLoader;

//#region Utils

/** @deprecated Implement me */
function warnNotImplemented() {
    showWarn("Chat method not implemented");
}

//#endregion

// cspell:words jivosite pipedrive
